// hooks/useSpotLinkUpdater.ts
import { useEffect } from "react";

interface UseSpotLinkUpdaterProps {
    /**
     * Your target domain (e.g., "ourmarketingsite.com")
     */
    targetDomain: string;
    /**
     * CSS selector for containers to monitor
     * Default: 'body' (monitors entire page)
     */
    containerSelector?: string;
    /**
     * Whether to preserve existing query params on target links
     * Default: true
     */
    preserveExistingParams?: boolean;
    /**
     * Whether to monitor for dynamically added content
     * Default: true
     */
    observeMutations?: boolean;
    /**
     * Additional domains to include (beyond the main targetDomain)
     */
    additionalDomains?: string[];
}

export const useSpotLinkUpdater = ({
    targetDomain,
    containerSelector = "body",
    preserveExistingParams = true,
    observeMutations = true,
    additionalDomains = []
}: UseSpotLinkUpdaterProps) => {
    useEffect(() => {
        const allTargetDomains = [targetDomain, ...additionalDomains];

        const enhanceLinks = (container: Element | Document) => {
            // Build selector for all target domains
            const domainSelectors = allTargetDomains.map(domain => `a[href*="${domain}"]:not([data-query-enhanced])`).join(", ");

            const targetLinks = container.querySelectorAll(domainSelectors);

            // Get current query string
            const currentSearch = window.location.search;

            if (!currentSearch) return; // No query params to append

            targetLinks.forEach(link => {
                const anchor = link as HTMLAnchorElement;
                const href = anchor.getAttribute("href");

                if (!href) return;

                try {
                    // Handle both absolute and relative URLs
                    let url: URL;

                    if (href.startsWith("http")) {
                        url = new URL(href);
                    } else if (href.startsWith("//")) {
                        url = new URL(`https:${href}`);
                    } else if (href.startsWith("/")) {
                        // Relative URL - need to check if it belongs to target domain
                        // Skip relative URLs unless they're for the current domain
                        const currentDomain = window.location.hostname;
                        if (!allTargetDomains.some(domain => currentDomain.includes(domain))) {
                            return;
                        }
                        url = new URL(href, window.location.origin);
                    } else {
                        // Assume it's a domain without protocol
                        url = new URL(`https://${href}`);
                    }

                    // Verify this link points to one of our target domains
                    const isTargetDomain = allTargetDomains.some(domain => url.hostname.includes(domain));

                    if (!isTargetDomain) return;

                    // Get current page's search params
                    const currentParams = new URLSearchParams(currentSearch);

                    // Handle param merging
                    if (preserveExistingParams) {
                        // Current params are added only if not already present
                        currentParams.forEach((value, key) => {
                            if (!url.searchParams.has(key)) {
                                url.searchParams.set(key, value);
                            }
                        });
                    } else {
                        // Replace entirely with current params
                        url.search = currentSearch;
                    }

                    // Update the link href
                    anchor.href = url.toString();

                    // Mark as enhanced to prevent re-processing
                    anchor.setAttribute("data-query-enhanced", "true");
                } catch (error) {
                    console.warn("Failed to enhance link:", href, error);
                }
            });
        };

        // Initial enhancement of existing links
        const containers = containerSelector === "body" ? [document] : Array.from(document.querySelectorAll(containerSelector));

        containers.forEach(enhanceLinks);

        // Set up MutationObserver if requested
        let observer: MutationObserver | null = null;

        if (observeMutations) {
            observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;

                            // If the added node matches our container selector, enhance it
                            if (containerSelector === "body" || element.matches?.(containerSelector)) {
                                enhanceLinks(element);
                            }

                            // Also check if it contains matching containers
                            if (containerSelector !== "body") {
                                const matchingContainers = element.querySelectorAll?.(containerSelector);
                                matchingContainers?.forEach(enhanceLinks);
                            } else {
                                // For body selector, just enhance the element itself
                                enhanceLinks(element);
                            }
                        }
                    });
                });
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }

        // Cleanup
        return () => {
            observer?.disconnect();
        };
    }, [targetDomain, containerSelector, preserveExistingParams, observeMutations, additionalDomains]);
};
